
import { useRef, useEffect, useState } from "react";
import { PenLine, FileText, Info, Tag, Monitor, Table2, FileEdit, Columns, RefreshCw, Moon } from "lucide-react";
import "../../styles/setting.css";
import UpdateSignature from "./Updatesignature";
import ContactInfo from "./Contactinfo";
import AddTags from "./AddTags";
import ViewerSetting from "./ViewerSetting";
import StudyTableSetting from "./StudyTableSetting";
import ReportEditorSetting from "./ReportEditorSetting";
import CustomizeTableColumns from "./CustomizeTableColumns";
import EnableDarkTheme from "./EnableDarkTheme";

const MENU_ITEMS = [
  { label: "Update signature", icon: PenLine },
  // { label: "Update Report", icon: FileText },
  // { label: "Contact info", icon: Info },
  // { label: "Add Tags", icon: Tag },
  // { label: "Viewer setting", icon: Monitor },
  // { label: "Study Table setting", icon: Table2 },
  // { label: "Report editor setting", icon: FileEdit },
  // { label: "Customize Table Columns", icon: Columns },
  // { label: "Auto refresh is off", icon: RefreshCw },
];

export default function SettingsDropdown({
  isOpen,
  onClose,
  onSelect,
  currentSign,
  activeToolId = 0, // Add this prop to know which table is active (0 = Study List, 1 = Report Summary)
  onColumnsCustomized, // Callback to refresh table after customization
}) {
  const dropdownRef = useRef(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [contactInfoOpen, setContactInfoOpen] = useState(false);
  const [addTagsOpen, setAddTagsOpen] = useState(false);
  const [viewerSettingOpen, setViewerSettingOpen] = useState(false);
  const [studyTableOpen, setStudyTableOpen] = useState(false);
  const [reportEditorOpen, setReportEditorOpen] = useState(false);
  const [customizeColsOpen, setCustomizeColsOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  
  /* Close on outside click */
  useEffect(() => {
    if (!isOpen) return;
    function handleOutside(e) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        onClose?.();
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () =>
      document.removeEventListener("mousedown", handleOutside);
  }, [isOpen, onClose]);
  
  /* Close on Escape */
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e) {
      if (e.key === "Escape") {
        onClose?.();
      }
    }
    document.addEventListener("keydown", handleKey);
    return () =>
      document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);
  
  function handleItemClick(label) {
    if (label === "Update signature") {
      setSignatureModalOpen(true);
      onClose?.();
      return;
    }
    if (label === "Contact info") {
      setContactInfoOpen(true);
      onClose?.();
      return;
    }
    if (label === "Add Tags") {
      setAddTagsOpen(true);
      onClose?.();
      return;
    }
    if (label === "Viewer setting") {
      setViewerSettingOpen(true);
      onClose?.();
      return;
    }
    if (label === "Study Table setting") {
      setStudyTableOpen(true);
      onClose?.();
      return;
    }
    if (label === "Report editor setting") {
      setReportEditorOpen(true);
      onClose?.();
      return;
    }
    if (label === "Customize Table Columns") {
      setCustomizeColsOpen(true);
      onClose?.();
      return;
    }
    /* Dark Theme Toggle */
    if (
      label === "Enable Dark theme" ||
      label === "Disable Dark theme"
    ) {
      const nextDarkState = !isDark;
      setIsDark(nextDarkState);
      if (nextDarkState) {
        document.body.classList.add("dark-theme");
      } else {
        document.body.classList.remove("dark-theme");
      }
      onClose?.();
      return;
    }
    onSelect?.(label);
    onClose?.();
  }
  
  function handleSignatureSubmit({ footerText, signFile }) {
    console.log("Signature updated:", {
      footerText,
      signFile,
    });
  }
  
  // Handle column customization submission
  const handleColumnsCustomized = (config) => {
    console.log("Table columns customized:", config);
    // Trigger refresh callback if provided
    if (onColumnsCustomized) {
      onColumnsCustomized(config);
    }
     setCustomizeColsOpen(false);
  };
  
  // Determine which table type is currently active for the modal
  const getCurrentTableType = () => {
    return activeToolId === 0 ? "Study List" : "Report Summary";
  };
  
  return (
    <>
      {isOpen && (
        <div
          ref={dropdownRef}
          className="settings-dropdown"
        >
          {[
            ...MENU_ITEMS,
            // {
            //   label: isDark
            //     ? "Disable Dark theme"
            //     : "Enable Dark theme",
            //   icon: Moon,
            // },
          ].map(({ label, icon: Icon }) => (
            <button
              key={label}
              className="settings-menu-item"
              onClick={() => handleItemClick(label)}
            >
              <Icon
                size={15}
                className="settings-item-icon"
              />
              {label}
            </button>
          ))}
        </div>
      )}
     
      <UpdateSignature
        isOpen={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        currentSign={currentSign}
        onSubmit={handleSignatureSubmit}
      />
      
     
      <ContactInfo
        isOpen={contactInfoOpen}
        onClose={() => setContactInfoOpen(false)}
        onSubmit={({
          email,
          contactNumber,
          address,
          gstNumber,
        }) => {
          console.log("Contact info submitted:", {
            email,
            contactNumber,
            address,
            gstNumber,
          });
        }}
      />
     
      <AddTags
        isOpen={addTagsOpen}
        onClose={() => setAddTagsOpen(false)}
        onSubmit={(tags) => {
          console.log("Tags submitted:", tags);
        }}
      />
      
     
      <ViewerSetting
        isOpen={viewerSettingOpen}
        onClose={() => setViewerSettingOpen(false)}
        onSubmit={(settings) => {
          console.log(
            "Viewer settings submitted:",
            settings
          );
        }}
      />
     
      <StudyTableSetting
        isOpen={studyTableOpen}
        onClose={() => setStudyTableOpen(false)}
        onSubmit={({ radiantPath, colors }) => {
          console.log(
            "Study Table settings submitted:",
            {
              radiantPath,
              colors,
            }
          );
        }}
      />
      
    
      <ReportEditorSetting
        isOpen={reportEditorOpen}
        onClose={() => setReportEditorOpen(false)}
        onSubmit={(data) => {
          console.log(
            "Report Editor settings submitted:",
            data
          );
        }}
      />
      
      <CustomizeTableColumns
        isOpen={customizeColsOpen}
        onClose={() => setCustomizeColsOpen(false)}
        onSubmit={handleColumnsCustomized}
        tableType={getCurrentTableType()}
        activeToolId={activeToolId}
      />
      
      <EnableDarkTheme
        isDark={isDark}
        setIsDark={setIsDark}
      />
    </>
  );
}